import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/components/AuthContext';
import { LoadingState } from '@/components/interactions';
import { useProfileSnapshot } from '@/hooks/api/useProfileSnapshot';
import {
  useReConsentStatus,
  isReConsentHard,
  isSessionDismissed,
} from '@/features/consent/reconsent';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // P1 OD-SNAPSHOT (SD-O68): live profile when the radio is up, the saved copy when it is not; `isLoading` is bounded
  // (8 s), so a farmer with a snapshot is never left on "Loading profile…" and never bounced for a read that could not run.
  const { data: profile, isLoading: profileLoading } = useProfileSnapshot({ enabled: !!user?.id });

  // Login-time re-consent gate (SD-RECONSENT-GATE). Detection only — the blocking
  // behaviour (soft/hard) is governed by the single switch in reconsent.ts.
  // FIX-SEASON-GATE-3 (E1): the read is never saved (FIX-SEASON-GATE D1), so it must never block
  // rendering — needsReConsent is true only after a SUCCESSFUL read says so.
  const { needsReConsent } = useReConsentStatus(
    user?.id,
    profile?.country_id ?? null,
  );

  if (isLoading) {
    return <LoadingState message={t('common.loading', 'Loading...')} fullScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (user?.id && profileLoading) {
    return <LoadingState message={t('common.loading', 'Loading...')} fullScreen />;
  }

  if (profile && profile.is_onboarded === false && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />;
  }

  // Re-consent gate — evaluated after onboarding, and skipped on the gate screen
  // itself so it cannot loop. Hard mode always redirects until re-accepted; soft
  // mode allows a per-session dismissal ("Skip for now") until the soft budget is
  // spent (see reconsent.ts). Fails open while resolving / on resolver error: a pending or
  // failed read renders the screen at once and never redirects (FIX-SEASON-GATE-3 E1).
  if (user?.id && !location.pathname.startsWith('/re-consent')) {
    if (needsReConsent) {
      const hard = isReConsentHard(user.id);
      if (hard || !isSessionDismissed(user.id)) {
        return <Navigate to="/re-consent" replace />;
      }
    }
  }

  return <>{children}</>;
};
